// src/pages/CustomerCaseStudy.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { PortableText, PortableTextReactComponents } from "@portabletext/react";
import { Helmet } from "react-helmet-async";

import { client } from "../sanity/client";
import { urlFor } from "../sanity/image";
import Button from "../shared/button/Button";
import {
  ArrowLeftIcon,
  CalendarDotsIcon,
  QuotesIcon,
} from "@phosphor-icons/react/dist/ssr";
import Title from "../components/typography/Title";
import Subtitle from "../components/typography/Subtitle";
import InitialsLogo from "../shared/InitialsLogo";
import Description from "../components/typography/Description";

type SanityImage = {
  alt?: string;
  asset?: {
    url?: string;
    metadata?: { dimensions?: { width?: number; height?: number } };
  };
};

type SanityFile = {
  asset?: {
    url?: string;
  };
};

type TocSubItem = {
  title?: string;
  anchor?: string;
};

type TocItem = {
  title?: string;
  anchor?: string;
  subItems?: TocSubItem[];
};

// ★ Roles / Products block type
type RolesProductsBlock = {
  sectionTitle?: string;
  roleColumnLabel?: string;
  productColumnLabel?: string;
  roles?: { name?: string }[];
  products?: { name?: string }[];
};

type CaseStudy = {
  hero?: {
    customerName?: string;
    segment?: string;
    headline?: string;
    subheadline?: string;
    author?: string;
    publishedAt?: string;
    heroImage?: SanityImage | any;
    heroVideoUrl?: string;
    heroVideoFile?: SanityFile | null;
  };
  metrics?: {
    label?: string;
    description?: string;
  }[];

  tableOfContents?: TocItem[];

  rolesProducts?: RolesProductsBlock;

  company?: {
    logo?: SanityImage | any;
    name?: string;
    industry?: string;
    headquarters?: string;
    intro?: string;
  };
  testimonial?: {
    quote?: string;
    personName?: string;
    personTitle?: string;
  };
  sections?: {
    title?: string;
    anchor?: string;
    image?: SanityImage | any;
    body?: any;
  }[];

  // SEO
  tabTitle?: string;
  seoTitle?: string;
  seoDescription?: string;
  seoImage?: SanityImage | null;
  seoImages?: SanityImage[] | null;
  canonical?: string;
  noIndex?: boolean;
  aiSummary?: string;
  focusKeyword?: string;
  metaKeywords?: string[];
  breadcrumbLabel?: string;

  // LLM
  aiSummaryLong?: string;
  llmTags?: string[];
  llmDifficulty?: string;
  llmEntities?: string[];
  llmQuestions?: string[];
  llmTakeaways?: string[];

  slug?: { current?: string };
};

type CaseStudyLite = {
  slug?: { current?: string };
  hero?: { customerName?: string };
};

const CASE_STUDY_BY_SLUG = `
*[_type == "customerCaseStudy" && slug.current == $slug][0]{
  hero,
  metrics,
  tableOfContents,

  rolesProducts{
    sectionTitle,
    roleColumnLabel,
    productColumnLabel,
    roles[]{ name },
    products[]{ name }
  },

  company,
  testimonial,
  sections,

  // SEO
  tabTitle,
  seoTitle,
  seoDescription,
  seoImage{
    alt,
    asset->{url, metadata{dimensions}}
  },
  seoImages[]{
    alt,
    asset->{url, metadata{dimensions}}
  },
  canonical,
  noIndex,
  aiSummary,
  focusKeyword,
  metaKeywords,
  breadcrumbLabel,

  // LLM
  aiSummaryLong,
  llmTags,
  llmDifficulty,
  llmEntities,
  llmQuestions,
  llmTakeaways,

  slug
}
`;

const ALL_CASE_STUDIES = `
*[_type == "customerCaseStudy"]{
  hero{ customerName },
  slug
}
`;

const getYouTubeId = (url?: string): string | null => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.replace("/", "");
    if (u.searchParams.get("v")) return u.searchParams.get("v");
    if (u.pathname.startsWith("/embed/")) return u.pathname.replace("/embed/", "");
    return null;
  } catch {
    return null;
  }
};

export default function CustomerCaseStudy() {
  const { slug = "" } = useParams();
  const [data, setData] = useState<CaseStudy | null>(null);
  const [allCaseStudies, setAllCaseStudies] = useState<CaseStudyLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [openTocIndex, setOpenTocIndex] = useState<number | null>(0);

  useEffect(() => {
    const s = decodeURIComponent(slug);
    setLoading(true);

    Promise.all([
      client.fetch<CaseStudy | null>(CASE_STUDY_BY_SLUG, { slug: s }),
      client.fetch<CaseStudyLite[]>(ALL_CASE_STUDIES),
    ])
      .then(([doc, all]) => {
        setData(doc);
        setAllCaseStudies(all || []);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const hero = data?.hero || {};
  const metrics = data?.metrics || [];
  const tocItems = data?.tableOfContents || [];
  const rolesProducts = data?.rolesProducts || {};
  const company = data?.company || {};
  const testimonial = data?.testimonial || {};
  const sections = data?.sections || [];

  // HERO video / image
  const heroVideoFileUrl = hero.heroVideoFile?.asset?.url || null;
  const ytId = getYouTubeId(hero.heroVideoUrl);

  const heroThumb =
    hero.heroImage &&
    urlFor(hero.heroImage as any).width(720).auto("format").url();

  const heroLogo =
    company.logo && urlFor(company.logo as any).width(140).height(40).auto("format").url();

  const heroSegmentText =
    hero.segment || (hero.customerName ? `Customers / ${hero.customerName}` : "Customers");

  const dateText =
    hero.publishedAt &&
    new Date(hero.publishedAt).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  // SEO helpers
  const baseUrl = "https://babblebots.ai";

  const canonical = (
    data?.canonical || `${baseUrl}/customers/${data?.slug?.current ?? slug}`
  ).replace(/\/$/, "");

  const primaryImage =
    data?.seoImage?.asset?.url || heroThumb || `${baseUrl}/default-og.jpg`;

  const metaTitle =
    data?.tabTitle ||
    data?.seoTitle ||
    hero.headline ||
    `${hero.customerName || ""} – Babblebots Customer Case Study`;

  const metaDesc =
    data?.seoDescription ||
    data?.aiSummary ||
    company.intro ||
    "How Babblebots AI-powered hiring helps customers improve recruitment speed, quality and cost.";

  // ✅ DO-OR-DIE: only keep valid OG image objects (urls exist)
  const ogImages = [data?.seoImage, ...(data?.seoImages || [])].filter(
    (img): img is SanityImage => !!img?.asset?.url
  );

  // ✅ DO-OR-DIE: JSON-LD image must never be []
  const jsonLdImages =
    ogImages.map((img) => img.asset?.url).filter(Boolean).length > 0
      ? (ogImages.map((img) => img.asset?.url).filter(Boolean) as string[])
      : [primaryImage];

  const breadcrumb = [
    { label: "Home", href: "/" },
    { label: "Customers", href: "/customers" },
    {
      label: data?.breadcrumbLabel || hero.customerName || "Customer Story",
      href: canonical,
    },
  ];

  const authorName = hero.author || "Team Babblebots";
  const publisherName = "Babblebots.ai";

  // ✅ DO-OR-DIE: remove document.title override (Helmet must own it)
  // const docTitle = useMemo(
  //   () => (metaTitle ? `${metaTitle} | Babblebots` : "Babblebots"),
  //   [metaTitle]
  // );
  // useEffect(() => {
  //   document.title = docTitle;
  // }, [docTitle]);

  // PortableText renderers
  const PT: Partial<PortableTextReactComponents> = {
    types: {
      image: ({ value }) => {
        if (!value?.asset) return null;
        const src = urlFor(value).width(1200).auto("format").url();
        return (
          <figure className="my-6">
            <div className="relative w-full overflow-hidden rounded-xl">
              <img
                src={src}
                alt={value?.alt || hero.headline}
                className="h-auto w-full object-cover"
                loading="lazy"
              />
            </div>
            {value?.caption && (
              <figcaption className="mt-2 text-sm text-gray-500">
                {value.caption}
              </figcaption>
            )}
          </figure>
        );
      },
      videoEmbed: ({ value }) => {
        const url: string | undefined = value?.url;
        if (!url) return null;

        const id = getYouTubeId(url);
        if (id) {
          return (
            <div className="my-6 aspect-video w-full overflow-hidden rounded-xl bg-black">
              <iframe
                className="h-full w-full"
                src={`https://www.youtube.com/embed/${id}?rel=0`}
                title={value?.title || hero.headline || "Customer video"}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          );
        }

        return (
          <div className="my-6 w-full">
            <video controls className="h-auto w-full rounded-xl" src={url}>
              Your browser does not support the video tag.
            </video>
          </div>
        );
      },
    },
    block: {
      h2: ({ children }) => (
        <h2 className="mb-3 mt-8 text-left text-3xl font-semibold text-gray-900">
          {children}
        </h2>
      ),
      h3: ({ children }) => (
        <h3 className="mb-2 mt-6 text-left text-2xl font-medium text-gray-900">
          {children}
        </h3>
      ),
      normal: ({ children }) => (
        <p className="mb-3 leading-relaxed text-gray-800">{children}</p>
      ),
    },
    list: {
      bullet: ({ children }) => (
        <ul className="my-2 ml-4 list-disc space-y-1 text-gray-800">{children}</ul>
      ),
      number: ({ children }) => (
        <ol className="my-2 ml-4 list-decimal space-y-1 text-gray-800">{children}</ol>
      ),
      checkmarks: ({ children }) => (
        <ol className="m-auto text-lg text-gray-800">{children}</ol>
      ),
    },
    marks: {
      strong: ({ children }) => <strong>{children}</strong>,
      em: ({ children }) => <em>{children}</em>,
      underline: ({ children }) => <span className="underline">{children}</span>,
      code: ({ children }) => (
        <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.85em]">
          {children}
        </code>
      ),
      highlight: ({ children }) => <span className="bg-yellow-200">{children}</span>,
      link: ({ children, value }) => {
        const href = value?.href || "#";
        const external = /^https?:\/\//i.test(href);
        return (
          <a
            href={href}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
            className="text-blue-600 underline hover:text-blue-800"
          >
            {children}
          </a>
        );
      },
    },
  };

  if (loading) {
    return (
      <main className="h-auto w-screen overflow-y-auto">
        <div className="flex h-[50vh] items-center justify-center text-white">
          Loading case study…
        </div>
      </main>
    );
  }

  // If no direct match on slug, try to auto-map to the right customerCaseStudy.
  // If still not found → show real 404 (noindex) instead of redirecting to blog.
  if (!data) {
    const normSlug = decodeURIComponent(slug).toLowerCase();
    const slugNoHyphen = normSlug.replace(/-/g, "");

    const match = allCaseStudies.find((cs) => {
      const rawName = cs.hero?.customerName;
      if (!rawName) return false;
      const name = rawName.toLowerCase().replace(/\s+/g, "");
      return slugNoHyphen.includes(name);
    });

    if (match?.slug?.current) {
      return <Navigate to={`/customers/${match.slug.current}`} replace />;
    }

    return (
      <main className="h-auto w-screen overflow-y-auto">
        <Helmet>
          <title>Customer story not found | Babblebots</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>

        <div className="flex h-[60vh] flex-col items-center justify-center gap-4 text-center">
          <Title>Customer story not found</Title>
          <p className="max-w-md text-gray-500">
            This customer case study may have been unpublished or moved.
          </p>
          <Link to="/customers" className="text-blue-600 underline">
            View all customer stories
          </Link>
        </div>
      </main>
    );
  }

  const hasRoles = (rolesProducts.roles && rolesProducts.roles.length > 0) || false;
  const hasProducts =
    (rolesProducts.products && rolesProducts.products.length > 0) || false;

  return (
    <>
      {/* SEO + OG + JSON-LD */}
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />

        {/* ✅ DO-OR-DIE: satisfy “Author/Publisher missing” tools */}
        <meta name="author" content={authorName} />
        <meta name="publisher" content={publisherName} />
        <meta property="article:author" content={authorName} />
        <meta property="article:publisher" content={baseUrl} />
        {hero.publishedAt && (
          <meta property="article:published_time" content={hero.publishedAt} />
        )}
        {hero.publishedAt && (
          <meta property="article:modified_time" content={hero.publishedAt} />
        )}

        {data.metaKeywords && data.metaKeywords.length > 0 && (
          <meta name="keywords" content={data.metaKeywords.join(", ")} />
        )}
        {data.focusKeyword && (
          <meta name="article:section" content={data.focusKeyword} />
        )}

        <link rel="canonical" href={canonical} />

        <meta
          name="robots"
          content={data.noIndex ? "noindex, nofollow" : "index, follow"}
        />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="Babblebots.ai" />

        {/* ✅ DO-OR-DIE: always output a valid og:image */}
        <meta property="og:image" content={ogImages[0]?.asset?.url || primaryImage} />

        {ogImages.map((img, i) => (
          <React.Fragment key={i}>
            <meta property="og:image" content={img.asset?.url || ""} />
            {img.alt && <meta property="og:image:alt" content={img.alt} />}
            {img.asset?.metadata?.dimensions?.width && (
              <meta
                property="og:image:width"
                content={String(img.asset.metadata.dimensions.width)}
              />
            )}
            {img.asset?.metadata?.dimensions?.height && (
              <meta
                property="og:image:height"
                content={String(img.asset.metadata.dimensions.height)}
              />
            )}
          </React.Fragment>
        ))}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content={ogImages[0]?.asset?.url || primaryImage} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: breadcrumb.map((b, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: b.label,
              item: b.href.startsWith("http") ? b.href : `${baseUrl}${b.href}`,
            })),
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: hero.headline,
            description: metaDesc,
            image: jsonLdImages,
            author: { "@type": "Person", name: authorName },
            publisher: {
              "@type": "Organization",
              name: publisherName,
              logo: {
                "@type": "ImageObject",
                url: `${baseUrl}/logominor.png`,
              },
            },
            datePublished: hero.publishedAt,
            dateModified: hero.publishedAt,
            mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
          })}
        </script>
      </Helmet>

      <main className="h-auto w-screen overflow-y-auto">
        {/* HERO */}
        <section className="relative flex h-auto w-screen items-center justify-center gap-10 overflow-hidden px-4 py-5 sm:px-6 md:h-[70vh] md:px-10">
          <div className="gradient-bg absolute left-0 top-2/3 h-1/2 w-full md:left-1/2 md:h-[65%] md:w-[65%] md:-translate-x-1/2" />

          <div className="z-30 w-full space-y-5 md:w-1/3">
            <Subtitle className="text-left">{heroSegmentText?.toUpperCase()}</Subtitle>
            <Title as="h1" className="text-left">
              {hero.headline}
            </Title>
            <div className="mb-4 mt-6">
              <InitialsLogo
                className="mr-4"
                name={hero.author ?? "Team Babblebots"}
                imgSrc={undefined}
              />
              <span>{hero?.author ?? "Team Babblebots"}</span>
            </div>
            <div className="flex w-full items-center gap-2">
              <CalendarDotsIcon size={16} color="#cfcfcf" />
              <span className="text-sm text-gray-300">{dateText}</span>
            </div>
          </div>

          <div className="z-30 h-full w-full rounded-xl md:w-2/3">
            {heroVideoFileUrl ? (
              <video
                className="h-full w-full rounded-xl object-cover"
                src={heroVideoFileUrl}
                controls
                playsInline
                poster={heroThumb}
              >
                Your browser does not support the video tag.
              </video>
            ) : ytId ? (
              <div className="h-full w-full overflow-hidden rounded-xl bg-black">
                <iframe
                  className="h-full w-full"
                  src={`https://www.youtube.com/embed/${ytId}?rel=0`}
                  title={hero.headline || "Customer video"}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : heroThumb ? (
              <img
                className="h-full w-full rounded-xl object-contain"
                src={heroThumb}
                alt={hero.headline}
              />
            ) : null}
          </div>
        </section>

        {/* METRICS */}
        {metrics.length > 0 && (
          <section className="flex w-full divide-x divide-gray-300 border-y border-gray-300 bg-white">
            {metrics.map((stat, i) => (
              <div key={i} className="flex flex-1 flex-col items-center gap-2 p-6">
                <div className="text-4xl font-semibold text-gray-700">
                {stat?.label}
                </div>
                <Description className="text-gray-700">{stat?.description}</Description>
              </div>
            ))}
          </section>
        )}

        {/* BODY */}
        <article className="flex w-screen flex-col bg-white px-4 pb-16 pt-10 text-black sm:flex-row sm:px-6 md:px-32 md:pt-32">
          {/* LEFT: Back + H1 + ROLES/PRODUCTS */}
          <aside className="mb-8 w-full sm:mb-0 sm:w-[32%] sm:pr-8">
            {/* ✅ Back button should return to /customers */}
            <Link to="/customers">
              <Button
                variant="secondary"
                className="mb-6 border-gray-450 !text-black hover:bg-gray-200 hover:shadow-md"
                icon={<ArrowLeftIcon size={20} />}
              >
                <span className="sr-only">Back to customers</span>
              </Button>
            </Link>

            {hero.headline && (
              <div className="mb-4 text-lg font-semibold leading-snug text-gray-900">
              {hero.headline}
              </div>
            )}


            {hasRoles && (
              <section className="mb-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
                <h3 className="mb-3 text-sm font-semibold text-gray-700">
                  {rolesProducts.sectionTitle || company.name || "Overview"}
                </h3>

                <div className="mb-3 h-1 w-full rounded-full bg-gray-200">
                  <div className="h-1 w-2/3 rounded-full bg-gray-400" />
                </div>

                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                    {rolesProducts.roleColumnLabel || "Role"}
                  </div>
                  <div className="mt-3 space-y-2">
                    {rolesProducts.roles?.map((r, i) => (
                      <div
                        key={i}
                        className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-900"
                      >
                        <span className="block truncate">{r.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {hasProducts && (
              <section className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                  {rolesProducts.productColumnLabel || "Product"}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {rolesProducts.products?.map((p, i) => (
                    <span
                      key={i}
                      className="rounded-full border border-gray-900 bg-white px-3 py-1 text-xs text-gray-900"
                    >
                      {p.name}
                    </span>
                  ))}
                </div>
              </section>
            )}
          </aside>

          {/* RIGHT: Company, quote, sections */}
          <div className="w-full sm:w-[68%]">
            <section className="mb-8 border-b border-gray-200 pb-6">
              <div className="mb-10 flex h-28 w-full">
                <div className="flex h-full flex-1 flex-col items-center justify-center gap-2">
                  {heroLogo ? (
                    <img
                      src={heroLogo}
                      alt={company.name || "Company logo"}
                      className="h-auto w-auto object-contain"
                    />
                  ) : null}
                  {company.name && <h3 className="text-lg font-semibold">{company.name}</h3>}
                </div>

                {company?.industry && (
                  <div className="flex flex-1 flex-col items-center gap-2 p-6">
                    <Subtitle className="text-base tracking-widest text-gray-600">
                      Industry
                    </Subtitle>
                    <Description className="text-base text-gray-600">
                      {company?.industry}
                    </Description>
                  </div>
                )}

                {company?.headquarters && (
                  <div className="flex flex-1 flex-col items-center gap-2 p-6">
                    <Subtitle className="text-base tracking-widest text-gray-600">
                      Headquarters
                    </Subtitle>
                    <Description className="text-base text-gray-600">
                      {company?.headquarters}
                    </Description>
                  </div>
                )}
              </div>

              {company.intro && (
                <div className="mt-6 space-y-3 text-sm leading-relaxed text-gray-800">
                  {company.intro.split("\n").map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              )}
            </section>

            {testimonial.quote && (
              <section className="mb-10 rounded-xl bg-gray-50 p-6">
                <div className="mb-3 text-3xl leading-none text-gray-300">
                  <QuotesIcon
                    size={48}
                    className="-scale-x-100"
                    color="black"
                    weight="fill"
                  />
                </div>
                <p className="mb-4 text-base leading-relaxed text-gray-800">
                  {testimonial.quote}
                </p>
                <p className="text-sm font-semibold text-gray-900">
                  {testimonial.personName}{" "}
                  {testimonial.personTitle && (
                    <span className="ml-1 font-normal text-gray-600">
                      {testimonial.personTitle}
                    </span>
                  )}
                </p>
              </section>
            )}

            {sections.map((sec, i) => (
              <section key={i} id={sec.anchor || undefined} className="mb-10">
                {sec.title && (
                  <h2 className="mb-4 text-2xl font-semibold text-gray-900">
                    {sec.title}
                  </h2>
                )}

                {sec.image && (
                  <img
                    src={urlFor(sec.image as any).width(900).auto("format").url()}
                    alt={sec.title || ""}
                    className="mb-4 w-full rounded-lg object-cover"
                  />
                )}

                {sec.body && <PortableText value={sec.body} components={PT} />}
              </section>
            ))}
          </div>
        </article>
      </main>
    </>
  );
}